import React, { useState } from 'react'
import { useQuery, useMutation } from 'react-query'
import services from '@services'
const { RestService } = services

export default function CanadaTaxNumbersPage() {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [currentProvince, setCurrentProvince] = useState<ProvinceNumber | null>(
    null,
  )

  const numbersQuery = useQuery(
    'canadaTaxNumbers',
    async (): Promise<ProvinceNumber[]> => {
      return await RestService.get('/accounting/canada_registration_numbers')
    },
  )

  const onEdit = (provinceCode: string) => {
    const existingData = numbersQuery.data?.find(
      (item) => item.province === provinceCode,
    )
    const item = existingData || { province: provinceCode, number: '' }
    setCurrentProvince(item)
    setIsEditModalOpen(true)
  }

  return (
    <div className="mt-20">
      <h1 className="text-2xl font-bold mb-4">
        Canada GST/HST Registration Numbers
      </h1>
      {numbersQuery.isLoading ? (
        <p>Loading tax numbers...</p>
      ) : numbersQuery.error ? (
        <p className="text-red-600">Error loading tax numbers</p>
      ) : (
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b-2 border-t-2 border-solid border-gray-400 px-4 py-3 text-left font-bold">
                Province
              </th>
              <th className="border-b-2 border-t-2 border-solid border-gray-400 px-4 py-3 text-left font-bold">
                GST/HST Number
              </th>
              <th className="border-b-2 border-t-2 border-solid border-gray-400 px-4 py-3 text-left font-bold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {PROVINCES.map((province, index) => {
              const taxNumber =
                numbersQuery.data?.find(
                  (item) => item.province === province.code,
                )?.number || ''

              return (
                <tr
                  key={province.code}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className="border-b border-solid border-gray-300 px-4 py-3">
                    {province.code} - {province.name}
                  </td>
                  <td className="border-b border-solid border-gray-300 px-4 py-3">
                    {taxNumber ? (
                      taxNumber
                    ) : (
                      <span className="text-gray-400 italic">
                        Not registered
                      </span>
                    )}
                  </td>
                  <td className="border-b border-solid border-gray-300 px-4 py-3">
                    <button
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                      onClick={() => onEdit(province.code)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {isEditModalOpen && (
        <UpsertProvinceNumberModal
          provinceNumber={currentProvince}
          onSuccess={() => {
            setIsEditModalOpen(false)
            numbersQuery.refetch()
          }}
          onCancel={() => setIsEditModalOpen(false)}
        />
      )}
    </div>
  )
}

type ProvinceNumber = {
  province: string
  number: string
}

type UpsertProvinceNumberModalProps = {
  provinceNumber: ProvinceNumber | null
  onSuccess: () => void
  onCancel: () => void
}

function UpsertProvinceNumberModal({
  provinceNumber,
  onSuccess,
  onCancel,
}: UpsertProvinceNumberModalProps) {
  const [formData, setFormData] = useState<ProvinceNumber>({
    province: provinceNumber?.province || '',
    number: provinceNumber?.number || '',
  })
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const upsertNumberMutation = useMutation(
    async (provinceNumber: ProvinceNumber) => {
      return await RestService.post('/accounting/canada_registration_numbers', {
        province: provinceNumber.province,
        number: provinceNumber.number,
      })
    },
    {
      onSuccess: () => {
        onSuccess()
      },
      onError: (error: Error) => {
        setErrorMessage(
          error.message || 'Failed to save tax number. Please try again.',
        )
      },
    },
  )

  const onFieldsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)
    upsertNumberMutation.mutate(formData)
  }

  const isEdit = !!provinceNumber

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Province Tax Number</h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 focus:outline-none text-3xl font-bold h-10 w-10 flex items-center justify-center"
          >
            &times;
          </button>
        </div>

        {errorMessage && (
          <div
            className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
            role="alert"
          >
            <p>{errorMessage}</p>
          </div>
        )}

        <form onSubmit={onSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Province</label>
            <select
              name="province"
              value={formData.province}
              onChange={onFieldsChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
              disabled={isEdit} // Disable if editing existing province
            >
              <option value="">Select a province</option>
              {PROVINCES.map((province) => (
                <option key={province.code} value={province.code}>
                  {province.code} - {province.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">GST/HST Number</label>
            <input
              type="text"
              name="number"
              value={formData.number}
              onChange={onFieldsChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Enter tax number"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={upsertNumberMutation.isLoading}
              className="inline-flex items-center justify-center bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
            >
              {upsertNumberMutation.isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
]
